import { useCallback, useEffect, useRef } from "react";
import { gameBus, type CharacterId, type TouchState } from "../game/GameBus";
import { ArrowIcon, MagicIcon } from "./Icons";

interface TouchControlsProps {
  character: CharacterId;
  disabled?: boolean;
}

export function TouchControls({ character, disabled = false }: TouchControlsProps) {
  const stateRef = useRef<TouchState>({ left: false, right: false, jump: false, magic: false });

  const reset = useCallback(() => {
    stateRef.current = { left: false, right: false, jump: false, magic: false };
    gameBus.emit("touch:update", { ...stateRef.current });
  }, []);

  useEffect(() => {
    if (disabled) reset();
  }, [disabled, reset]);

  useEffect(() => reset, [reset]);

  const setAction = useCallback((action: keyof TouchState, active: boolean) => {
    if (disabled && active) return;
    stateRef.current = { ...stateRef.current, [action]: active };
    gameBus.emit("touch:update", { ...stateRef.current });
  }, [disabled]);

  const bind = (action: keyof TouchState) => ({
    onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      setAction(action, true);
    },
    onPointerUp: () => setAction(action, false),
    onPointerCancel: () => setAction(action, false),
    onLostPointerCapture: () => setAction(action, false)
  });

  return (
    <div className="touch-controls" aria-label="בקרי משחק">
      <div className="touch-group movement">
        <button className="touch-button" aria-label="שמאלה" disabled={disabled} {...bind("left")}><ArrowIcon direction="left" /></button>
        <button className="touch-button" aria-label="ימינה" disabled={disabled} {...bind("right")}><ArrowIcon direction="right" /></button>
      </div>
      <div className="touch-group actions">
        <button className="touch-button magic" aria-label="קסם" disabled={disabled} {...bind("magic")}><MagicIcon character={character} /></button>
        <button className="touch-button jump" aria-label="קפיצה" disabled={disabled} {...bind("jump")}><ArrowIcon direction="up" /></button>
      </div>
    </div>
  );
}
